import { err, ok, type Result } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';

import type { ITimelineCommands } from '@src/editor/interface/IVideoEditorCommands';
import type { Element } from '@src/editor/models/Element';

export type TrackVisibilityEditorLike = Pick<
  ITimelineCommands,
  'commandUpdateMultipleElements'
>;

export interface TrackVisibilityEditorStoreLike {
  editor: TrackVisibilityEditorLike | null;
  tracksMap: Map<number, Element[]>;
}

export interface TrackVisibilityVideoCreatorLike {
  isOriginalVideoElement?: (source: Record<string, any>) => boolean;
}

export interface TrackVisibilityServiceDeps {
  videoCreator: TrackVisibilityVideoCreatorLike;
  editorStore: TrackVisibilityEditorStoreLike;
}

export type ToggleTrackVisibilityResult = Result<{
  track: number;
  visible: boolean;
  elementIds: string[];
}>;

const logger = LogManager.getLogger('service:TrackVisibility');

export class TrackVisibilityService {
  private readonly _videoCreator: TrackVisibilityVideoCreatorLike;
  private readonly _editorStore: TrackVisibilityEditorStoreLike;

  constructor(deps: TrackVisibilityServiceDeps) {
    this._videoCreator = deps.videoCreator;
    this._editorStore = deps.editorStore;
  }

  toggleTrackVisibility(
    trackNumber: number,
    forceVisible?: boolean,
  ): ToggleTrackVisibilityResult {
    if (!Number.isFinite(trackNumber)) {
      return err(
        'VALIDATION',
        'Track number is required for visibility toggle',
      );
    }

    const elements = this._editorStore.tracksMap.get(trackNumber);
    if (!elements || elements.length === 0) {
      return err('NOT_FOUND', 'Track has no elements to toggle', {
        trackNumber,
      });
    }

    if (
      this._videoCreator.isOriginalVideoElement?.(elements[0].getState()) ===
      true
    ) {
      return err(
        'PRECONDITION',
        'Original video track cannot be hidden via visibility toggle',
        { trackNumber },
      );
    }

    const editor = this._editorStore.editor;
    if (!editor) {
      return err(
        'PRECONDITION',
        'Track visibility requires an active headless editor instance',
      );
    }

    const isCurrentlyHidden = this._isTrackHidden(elements);
    const targetVisible = forceVisible ?? isCurrentlyHidden;

    if (this._matchesVisibility(elements, targetVisible)) {
      return ok({ track: trackNumber, visible: targetVisible, elementIds: [] });
    }

    const updatesMap = this._buildUpdates(elements, targetVisible);
    if (updatesMap.size === 0) {
      logger.warn('No element ids discovered while toggling track visibility', {
        trackNumber,
      });
      return err(
        'UNKNOWN',
        'Track visibility could not be updated because elements were missing ids',
        { trackNumber },
      );
    }

    try {
      const success = editor.commandUpdateMultipleElements(updatesMap);
      if (!success) {
        logger.warn('Editor rejected track visibility updates', {
          trackNumber,
          targetVisible,
          elementCount: updatesMap.size,
        });
        return err(
          'UNKNOWN',
          'Track visibility could not be updated through editor command',
          { trackNumber },
        );
      }
    } catch (error) {
      logger.error('Failed to toggle track visibility', {
        trackNumber,
        targetVisible,
        error,
      });
      return err(
        'UNKNOWN',
        'Unexpected failure toggling track visibility',
        error,
      );
    }

    return ok({
      track: trackNumber,
      visible: targetVisible,
      elementIds: [...updatesMap.keys()],
    });
  }

  private _isTrackHidden(elements: Element[]): boolean {
    return elements.every(element => element.visible === false);
  }

  private _matchesVisibility(
    elements: Element[],
    targetVisible: boolean,
  ): boolean {
    return targetVisible
      ? elements.every(element => element.visible !== false)
      : this._isTrackHidden(elements);
  }

  private _buildUpdates(
    elements: Element[],
    visible: boolean,
  ): Map<string, Record<string, any>> {
    const updates = new Map<string, Record<string, any>>();
    for (const element of elements) {
      const elementId = element.id;
      if (!elementId) continue;
      updates.set(elementId, { visible });
    }
    return updates;
  }
}
